import type { Track } from '../types'

interface ScheduledVoice {
  source: AudioNode | AudioBufferSourceNode
  gain: GainNode
}

interface AudioEngineOptions {
  getState: () => { tracks: Track[]; tempo: number }
  onPosition: (position: number) => void
}

const STEPS = 16
const LOOKAHEAD_MS = 25
const SCHEDULE_AHEAD_S = 0.1
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const NOTE_FREQS: Record<string, number> = {
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, B5: 987.77,
}

const noteToFreq = (note: string): number => {
  if (note in NOTE_FREQS) return (NOTE_FREQS as Record<string, number>)[note]
  const midi = Number(note)
  if (!Number.isNaN(midi)) return 440 * 2 ** ((midi - 69) / 12)
  return 261.63
}

export class AudioEngine {
  private ctx: AudioContext | null = null
  private running = false
  private timer: number | null = null
  private currentStep = 0
  private nextStepTime = 0
  private voices: ScheduledVoice[] = []
  private masterGain: GainNode | null = null
  private panner: StereoPannerNode | null = null
  private limiter: DynamicsCompressorNode | null = null

  public async init() {
    if (this.ctx) return
    const Ctx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    this.ctx = new Ctx()
    this.masterGain = this.ctx.createGain()
    this.panner = this.ctx.createStereoPanner()
    this.limiter = this.ctx.createDynamicsCompressor()
    this.limiter.threshold.value = -4
    this.limiter.knee.value = 6
    this.limiter.ratio.value = 12
    this.limiter.attack.value = 0.003
    this.limiter.release.value = 0.25
    this.masterGain.connect(this.panner)
    this.panner.connect(this.limiter)
    this.limiter.connect(this.ctx.destination)
  }

  public async start(options: AudioEngineOptions) {
    await this.init()
    if (!this.ctx || this.running) return
    if (this.ctx.state === 'suspended') await this.ctx.resume()
    this.running = true
    this.currentStep = 0
    this.nextStepTime = this.ctx.currentTime + 0.05
    this.schedule(options)
    this.timer = window.setInterval(() => this.schedule(options), LOOKAHEAD_MS)
  }

  public stop() {
    this.running = false
    if (this.timer !== null) {
      window.clearInterval(this.timer)
      this.timer = null
    }
    for (const v of this.voices) {
      if (v.source instanceof AudioBufferSourceNode) {
        try { v.source.stop() } catch { /* ignore */ }
      }
      v.gain.disconnect()
    }
    this.voices = []
  }

  public setTempo(_tempo: number) {
    // Tempo is read from getState each step; keeping method signature for compatibility.
  }

  public setMaster(volume: number, pan: number, limiterEnabled: boolean) {
    if (!this.masterGain || !this.ctx || !this.panner || !this.limiter) return
    this.masterGain.gain.setTargetAtTime(clamp(volume, 0.0001, 1.5), this.ctx.currentTime, 0.02)
    this.panner.pan.setTargetAtTime(clamp(pan, -1, 1), this.ctx.currentTime, 0.03)
    this.limiter.threshold.setTargetAtTime(limiterEnabled ? -4 : 0, this.ctx.currentTime, 0.05)
  }

  private schedule(options: AudioEngineOptions) {
    if (!this.ctx || !this.masterGain) return
    while (this.nextStepTime < this.ctx.currentTime + SCHEDULE_AHEAD_S) {
      const { tracks, tempo } = options.getState()
      const anySolo = tracks.some((t) => t.soloed)
      for (const track of tracks) {
        if (!track.enabled) continue
        if (anySolo && !track.soloed) continue
        if (track.muted) continue
        const step = track.steps[this.currentStep % STEPS]
        if (!step?.enabled) continue
        this.trigger(track, step, this.nextStepTime, tempo)
      }
      options.onPosition(this.currentStep)
      this.currentStep = (this.currentStep + 1) % STEPS
      this.nextStepTime += (60 / tempo) / 4
    }
  }

  private trigger(track: Track, step: { note: string; velocity: number }, time: number, tempo: number) {
    if (!this.ctx || !this.masterGain) return
    const isBeatbox = track.instrumentId.startsWith('inst-beatbox') || track.type === 'drum'
    if (isBeatbox) {
      if (this.currentStep % 2 === 1) this.triggerHat(track, time)
      else this.triggerKick(track, time)
      return
    }

    const freq = noteToFreq(step.note)
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    const filter = this.ctx.createBiquadFilter()
    const params = { ...track.params }
    const vel = step.velocity / 100
    const dur = (60 / tempo) * 3.5

    osc.type = track.name.includes('Bass') ? 'sawtooth' : track.name.includes('Pad') ? 'sawtooth' : 'triangle'
    osc.frequency.setValueAtTime(freq, time)

    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(300 + (params.freq ?? 0.5) * 7000, time)
    filter.Q.setValueAtTime(0.5 + (params.reso ?? 0.25) * 14, time)

    const vol = clamp(track.volume * (params.wet ?? 0.2) * vel, 0.0001, 1)
    gain.gain.setValueAtTime(0.0001, time)
    gain.gain.exponentialRampToValueAtTime(vol, time + Math.max(0.005, params.attack ?? 0.02))
    gain.gain.exponentialRampToValueAtTime(0.0001, time + dur + (params.release ?? 0.2))

    osc.connect(filter)
    filter.connect(gain)
    gain.connect(this.masterGain)

    const trackPan = clamp(track.pan, -1, 1)
    if (this.panner) this.panner.pan.setTargetAtTime(trackPan, time, 0.01)

    osc.start(time)
    osc.stop(time + dur + 0.05)
    osc.onended = () => {
      gain.disconnect()
      filter.disconnect()
      osc.disconnect()
    }
    this.voices.push({ source: osc, gain })
  }

  private triggerKick(track: Track, time: number) {
    if (!this.ctx || !this.masterGain) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(110, time)
    osc.frequency.exponentialRampToValueAtTime(45, time + 0.12)
    const vel = track.volume * 0.9
    gain.gain.setValueAtTime(0.0001, time)
    gain.gain.exponentialRampToValueAtTime(vel, time + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.25)
    if (this.panner) this.panner.pan.setTargetAtTime(clamp(track.pan, -1, 1), time, 0.01)
    osc.connect(gain)
    gain.connect(this.masterGain)
    osc.start(time)
    osc.stop(time + 0.3)
    osc.onended = () => {
      gain.disconnect()
      osc.disconnect()
    }
    this.voices.push({ source: osc, gain })
  }

  private triggerHat(track: Track, time: number) {
    if (!this.ctx || !this.masterGain) return
    const bufSize = Math.floor(this.ctx.sampleRate * 0.07)
    const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'highpass'
    filter.frequency.value = 6000
    const gain = this.ctx.createGain()
    const vel = track.volume * 0.55
    gain.gain.setValueAtTime(0.0001, time)
    gain.gain.exponentialRampToValueAtTime(vel, time + 0.004)
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.06)
    if (this.panner) this.panner.pan.setTargetAtTime(clamp(track.pan, -1, 1), time, 0.01)
    src.connect(filter)
    filter.connect(gain)
    gain.connect(this.masterGain)
    src.start(time)
    src.stop(time + 0.07)
    if (src instanceof AudioBufferSourceNode) {
      src.onended = () => {
        gain.disconnect()
        filter.disconnect()
        src.disconnect()
      }
    }
    this.voices.push({ source: src, gain })
  }
}

export const audioEngine = new AudioEngine()
